import {
  Popover,
  Typography,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
} from 'antd';

import classnames from 'classnames';

import { WrapperSelect } from 'common/components';
import {
  QaSpecBoolean,
  QaSpecBooleanTrueFalse,
} from 'pages/qa-spec/components/shared/QaSpecBoolean';

import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';

import isEqual from 'lodash/isEqual';

import './RetailerSnapshot.less';

const { Option } = Select;

export const RetailerSnapshot = ({
  supplierModifiedValue,
  snapshotValue,
  highlightContent,
  children,
  ...rest
}) => {
  const isToggleSnapshot = useCheckSnapshotForRetailer();

  const isDataReady =
    supplierModifiedValue !== undefined && snapshotValue !== undefined;
  const isDiffValue =
    isDataReady && !isEqual(supplierModifiedValue, snapshotValue);

  let element = children;

  if (isToggleSnapshot && isDiffValue) {
    element = (
      <Popover
        overlayClassName='retailer-snapshot__popover'
        title={<Typography.Text strong>Previous Version</Typography.Text>}
        trigger='hover'
        overlayInnerStyle={{ minWidth: 400 }}
        content={highlightContent}
        {...rest}
      >
        <div className='retailer-snapshot__highlight'>{children}</div>
      </Popover>
    );
  }

  return element;
};

export const RetailerSnapshotInput = (props) => {
  const { className, ...rest } = props;

  return (
    <Input
      className={classnames({
        'retailer-snapshot__input': true,
        [className]: !!className,
      })}
      {...rest}
    />
  );
};

export const RetailerSnapshotInputNumber = (props) => {
  const { className, ...rest } = props;

  return (
    <InputNumber
      className={classnames({
        'retailer-snapshot__input-number': true,
        [className]: !!className,
      })}
      {...rest}
    />
  );
};

export const RetailerSnapshotTextarea = (props) => {
  const { className, ...rest } = props;

  return (
    <Input.TextArea
      className={classnames({
        'retailer-snapshot__input': true,
        [className]: !!className,
      })}
      {...rest}
    />
  );
};

export const RetailerSnapshotBoolean = (props) => {
  const { className, value, ...rest } = props;

  return (
    <QaSpecBoolean
      className={classnames({
        'retailer-snapshot__radio': true,
        [className]: !!className,
      })}
      value={value}
      {...rest}
    />
  );
};

export const RetailerSnapshotBooleanTrueFalse = (props) => {
  const { className, value, ...rest } = props;

  return (
    <QaSpecBooleanTrueFalse
      className={classnames({
        'retailer-snapshot__radio': true,
        [className]: !!className,
      })}
      value={value}
      {...rest}
    />
  );
};

export const RetailerSnapshotSelect = (props) => {
  const { className, children, ...rest } = props;

  return (
    <WrapperSelect
      className={classnames({
        'retailer-snapshot__select': true,
        [className]: !!className,
      })}
      {...rest}
    >
      {children}
    </WrapperSelect>
  );
};

export const RetailerSnapshotOption = (props) => {
  const { className, children, ...rest } = props;

  return (
    <Option
      className={classnames({
        'retailer-snapshot__select-option': true,
        [className]: !!className,
      })}
      {...rest}
    >
      {children}
    </Option>
  );
};

export const PrimaryFieldSnapshot = ({ variant = 'text', ...otherProps }) => {
  const isToggleSnapshot = useCheckSnapshotForRetailer();

  switch (variant) {
    case 'text':
      return isToggleSnapshot ? (
        <RetailerSnapshotInput {...otherProps} />
      ) : (
        <Input {...otherProps} />
      );

    case 'textarea':
      return isToggleSnapshot ? (
        <RetailerSnapshotTextarea {...otherProps} />
      ) : (
        <Input.TextArea {...otherProps} />
      );

    case 'number':
      return isToggleSnapshot ? (
        <RetailerSnapshotInputNumber {...otherProps} />
      ) : (
        <InputNumber {...otherProps} />
      );

    case 'select':
      return isToggleSnapshot ? (
        <RetailerSnapshotSelect {...otherProps} />
      ) : (
        <WrapperSelect {...otherProps} />
      );

    case 'checkbox':
      return isToggleSnapshot ? (
        <RetailerSnapshotBoolean {...otherProps} />
      ) : (
        <QaSpecBoolean {...otherProps} />
      );

    case 'checkbox-true-false':
      return isToggleSnapshot ? (
        <RetailerSnapshotBooleanTrueFalse {...otherProps} />
      ) : (
        <QaSpecBooleanTrueFalse {...otherProps} />
      );

    default:
      return <Input {...otherProps} />;
  }
};

export const HighlightContent = ({
  label,
  value,
  variant = 'text',
  ...otherProps
}) => {
  return (
    <Row gutter={[6, 0]} {...otherProps}>
      <Col>
        <Typography.Text strong>{label}</Typography.Text>
      </Col>
      <Col flex={1}>
        {variant === 'checkbox' ? (
          <HighlightContentBoolean value={value} />
        ) : variant === 'checkbox-true-false' ? (
          <HighlightContentBooleanTrueFalse value={value} />
        ) : (
          <Typography.Text>{value ?? 'N/A'}</Typography.Text>
        )}
      </Col>
    </Row>
  );
};

export const HighlightContentBoolean = ({ value }) => {
  return <QaSpecBoolean value={value} />;
};

export const HighlightContentBooleanTrueFalse = ({ value }) => {
  return <QaSpecBooleanTrueFalse value={value} />;
};